// Charts of report results (full reports; counts and sums the report builder can't show as a list): group the
// entities a report matches by a field value, by the entity at the other end of their links, or by year, and count
// them or sum the amounts of their money links in one currency (amounts are never converted). Three chart types,
// drawn as standalone SVG in the light print palette from the design tokens, like the graph and timeline snapshots,
// so the same picture shows on screen and goes into the PDF/DOCX. Every chart has a table equivalent.

import { FLOW_EXTRA_RELATIONS } from './flows';
import { parseReportQuery, type ReportQuery, reportFieldsFor } from './reports';
import type { FieldDef, FieldType, RelationTypeDef, SchemaSnapshot } from './schema/types';
import { isKey } from './schema/types';

export const CHART_TYPES = ['bar', 'stacked_bar', 'grouped_bar', 'line'] as const;
export type ChartType = (typeof CHART_TYPES)[number];
export const CHART_TYPE_LABELS: Record<ChartType, string> = {
  bar: 'Bar chart',
  stacked_bar: 'Stacked bar chart',
  grouped_bar: 'Grouped bar chart',
  line: 'Line chart',
};
/** Chart types that split each bar by a second dimension (stacked: one bar in segments; grouped: one bar per split value). */
export const splitsBars = (t: ChartType) => t === 'stacked_bar' || t === 'grouped_bar';

export const AGGREGATE_LIMITS = {
  /** Buckets returned (the rest is summed into "Other"). */
  buckets: 24,
  /** Split values per bucket (stacked bars). */
  splits: 8,
  /** Entities read to aggregate. */
  entities: 5000,
} as const;

/**
 * What a bucket is. `field`: a field value (list values, ratings, years of a date); `link_end`: the entity at the other
 * end of the entity's links (optionally only these relation keys); `year`: the year a link (or a date field) starts (a
 * link to an event with no date of its own takes the event's date); `role`: the participation role of the link
 * (speaker, participant, moderator, organizer…; optionally only these roles); `organizer`: who organized the event at
 * the other end of a participation link (for a report of events, the event itself).
 */
export type AggregateGroupBy =
  | { kind: 'field'; field: string }
  | { kind: 'link_end'; relations?: string[] }
  | { kind: 'year'; of: 'link'; relations?: string[] }
  | { kind: 'year'; of: 'field'; field: string }
  | { kind: 'role'; relations?: string[] }
  | { kind: 'organizer' };

/**
 * `count`: entities per bucket; `sum`: amounts of the entities' money links in one currency (ranges count as their
 * middle, undisclosed amounts as 0 and are reported); `links`: the entities' links (an appearance register's rows:
 * participation links, or only these relation keys), each counted once per bucket.
 */
export type AggregateMeasure =
  | { kind: 'count' }
  | { kind: 'sum'; currency: string; relations?: string[] }
  | { kind: 'links'; relations?: string[] };

/**
 * Rates normalised by group size, each shown beside the base rate of all the matched entities. `share`: the percentage
 * of each group's entities in each split value ("39% of EPP members vs 26% of all"; needs a split and the entity
 * count). `per_entity`: links or amounts per entity of the group ("3.2 appearances per EPP member vs 1.9 for all";
 * needs the links or sum measure).
 */
export const AGGREGATE_RATES = ['share', 'per_entity'] as const;
export type AggregateRate = (typeof AGGREGATE_RATES)[number];

export interface AggregateRequest {
  query: ReportQuery;
  groupBy: AggregateGroupBy;
  /** A second dimension for stacked bars, or null. */
  splitBy: AggregateGroupBy | null;
  measure: AggregateMeasure;
  /**
   * The report question the chart stays within (D395, D415): a section's starter question or a figure the report plan
   * suggests counts only what the report itself is about. Absent or null: none.
   */
  within?: ReportQuery | null;
  /** Rates instead of raw values (absent = raw counts or sums). */
  rate?: AggregateRate;
}

/** The rates of an aggregate: the buckets' `value` (per entity) or `split` (share) hold rates, and these say against what. */
export interface AggregateRateInfo {
  kind: AggregateRate;
  /** Base rate over every matched entity: one per split (share), or one number (per entity). */
  base: number[];
  /** Entities in each bucket (the group size each rate is divided by), in the order of `buckets`. */
  sizes: number[];
  /** Raw numbers behind the rates (entities per split for share, links or amounts for per entity), per bucket. */
  raw: number[][];
  /** Matched entities (the base rate's denominator). */
  population: number;
}

export interface AggregateBucket {
  key: string;
  label: string;
  value: number;
  /** Values per split (stacked bars), in the order of `AggregateResult.splits`. */
  split: number[] | null;
}

export interface AggregateResult {
  buckets: AggregateBucket[];
  /** Split labels (stacked bars), or empty. */
  splits: { key: string; label: string }[];
  /** "Entities", "Amount (EUR)". */
  measureLabel: string;
  /** "Camp", "Funded by", "Year". */
  groupLabel: string;
  /** Currencies seen in the money links (for the currency picker). */
  currencies: string[];
  /** Entities the report matched and the aggregate read. */
  entities: number;
  /** More buckets or entities than the limits: the rest is under "Other" or left out (see note). */
  truncated: boolean;
  /** What a reader must know (undisclosed amounts, other currencies left out…), or null. */
  note: string | null;
  /** Rates and their base rates, when the request asked for rates; null for raw values. */
  rate?: AggregateRateInfo | null;
  /** How the numbers were computed, in one or two plain sentences (shown beside the chart). */
  method?: string;
  /** Values are fractions to print with four decimals (betweenness), not counts or amounts. */
  format?: 'decimal';
}

/** Field types a chart can group by: list values, ratings, numbers, short text (Political group: same text, any case), and dates (by year). */
export const AGGREGATE_FIELD_TYPES: readonly FieldType[] = ['enum', 'multi_enum', 'rating', 'number', 'text', 'fuzzy_date'];

/** The money relations, as the money flows read them: amount-bearing relations of the flow category, plus "funded by". */
export function aggregateMoneyRelations(schema: Pick<SchemaSnapshot, 'relationTypes'>): RelationTypeDef[] {
  return schema.relationTypes.filter(t => t.hasAmount && (t.category === 'flow' || FLOW_EXTRA_RELATIONS.includes(t.key)));
}

class Invalid extends Error {}
const fail = (message: string): never => {
  throw new Invalid(message);
};
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const MAX_RELATIONS = 50;

function relationKeys(v: unknown, what: string, allowed: readonly RelationTypeDef[], wrong: (k: string) => string): string[] | undefined {
  if (v === undefined || v === null) return undefined;
  if (!Array.isArray(v)) return fail(`${what}: expected a list of relation types.`);
  if (v.length > MAX_RELATIONS) fail(`${what}: pick at most ${MAX_RELATIONS} relation types.`);
  const out = [...new Set(v)];
  for (const k of out) {
    if (!isKey(k)) fail(`${what}: "${String(k).slice(0, 40)}" is not a relation type.`);
    if (!allowed.some(t => t.key === k)) fail(wrong(k as string));
  }
  return out.length ? (out as string[]) : undefined;
}

function groupField(v: unknown, what: string, fields: FieldDef[], types: readonly FieldType[]): string {
  if (!isKey(v)) return fail(`${what}: pick a field.`);
  const def = fields.find(f => f.key === v);
  if (!def) return fail(`${what}: "${v}" is not a field of the entities this report lists.`);
  if (!types.includes(def.dataType))
    fail(
      types.length === 1
        ? `${what}: ${def.label} is not a date field.`
        : `${what}: ${def.label} can't be charted. Pick a list, rating, number, short text or date field.`,
    );
  return def.key;
}

/** Participation relation types (speaker, participant, organizer, vote_for…): the roles an appearance can have. */
export function participationRoles(schema: Pick<SchemaSnapshot, 'relationTypes'>): RelationTypeDef[] {
  return schema.relationTypes.filter(t => t.category === 'participation' && !t.archived);
}

function parseGroupBy(v: unknown, what: string, fields: FieldDef[], schema: SchemaSnapshot): AggregateGroupBy {
  if (!isObj(v)) return fail(`${what}: pick a field, the linked entities or the year.`);
  const relations = (r: unknown) => relationKeys(r, what, schema.relationTypes, k => `${what}: "${k}" is not a relation type.`);
  switch (v.kind) {
    case 'role': {
      const roles = relationKeys(v.relations, what, participationRoles(schema), k => `${what}: "${k}" is not a participation role.`);
      return roles ? { kind: 'role', relations: roles } : { kind: 'role' };
    }
    case 'organizer':
      return { kind: 'organizer' };
    case 'field':
      return { kind: 'field', field: groupField(v.field, what, fields, AGGREGATE_FIELD_TYPES) };
    case 'link_end': {
      const rel = relations(v.relations);
      return rel ? { kind: 'link_end', relations: rel } : { kind: 'link_end' };
    }
    case 'year':
      if (v.of === 'field') return { kind: 'year', of: 'field', field: groupField(v.field, what, fields, ['fuzzy_date']) };
      if (v.of === 'link') {
        const rel = relations(v.relations);
        return rel ? { kind: 'year', of: 'link', relations: rel } : { kind: 'year', of: 'link' };
      }
      return fail(`${what}: say whether the year is the year of a link or of a date field.`);
    default:
      return fail(`${what}: pick a field, the linked entities or the year.`);
  }
}

function parseMeasure(v: unknown, schema: SchemaSnapshot): AggregateMeasure {
  if (v === undefined || v === null) return { kind: 'count' };
  if (!isObj(v)) return fail('Measure: count the entities or sum their money links.');
  if (v.kind === 'count') return { kind: 'count' };
  if (v.kind === 'links') {
    const relations = relationKeys(v.relations, 'Measure', schema.relationTypes, k => `Measure: "${k}" is not a relation type.`);
    return relations ? { kind: 'links', relations } : { kind: 'links' };
  }
  if (v.kind !== 'sum') return fail('Measure: count the entities or sum their money links.');
  const currency = typeof v.currency === 'string' ? v.currency.trim().toUpperCase() : '';
  if (!/^[A-Z]{3}$/.test(currency)) fail('Measure: pick the currency to sum, as a 3-letter code (EUR, USD).');
  const money = aggregateMoneyRelations(schema);
  const relations = relationKeys(v.relations, 'Measure', money, k => {
    const t = schema.relationTypes.find(r => r.key === k);
    return t ? `Measure: ${t.label} is not a money relation.` : `Measure: "${k}" is not a relation type.`;
  });
  return relations ? { kind: 'sum', currency, relations } : { kind: 'sum', currency };
}

function parseRate(v: unknown, splitBy: AggregateGroupBy | null, measure: AggregateMeasure): AggregateRate | undefined {
  if (v === undefined || v === null || v === 'none') return undefined;
  if (!(AGGREGATE_RATES as readonly unknown[]).includes(v)) return fail('Rate: show shares of each group or values per entity.');
  if (v === 'share') {
    if (!splitBy) fail('Rate: a share needs something to split each group by (for example the participation role).');
    if (measure.kind !== 'count') fail('Rate: a share counts entities; pick the number of entities as the measure.');
  }
  if (v === 'per_entity' && measure.kind === 'count')
    fail('Rate: per entity divides links or amounts by the group size; pick the number of links or a sum as the measure.');
  return v as AggregateRate;
}

/** Checks an aggregate request (the query with parseReportQuery, the field keys against the schema). */
export function parseAggregateRequest(
  v: unknown,
  schema: SchemaSnapshot,
): { ok: true; value: AggregateRequest } | { ok: false; error: string } {
  try {
    if (!isObj(v)) fail('The chart request must be an object.');
    const r = v as Record<string, unknown>;
    const q = parseReportQuery(r.query, schema);
    if (!q.ok) return q;
    const fields = reportFieldsFor(schema, q.value.kind, q.value.types);
    const groupBy = parseGroupBy(r.groupBy, 'Group by', fields, schema);
    const splitBy = r.splitBy === undefined || r.splitBy === null ? null : parseGroupBy(r.splitBy, 'Split by', fields, schema);
    if (splitBy && JSON.stringify(splitBy) === JSON.stringify(groupBy)) fail('Split by something other than what the chart groups by.');
    const measure = parseMeasure(r.measure, schema);
    let within: ReportQuery | null = null;
    if (r.within !== undefined && r.within !== null) {
      const w = parseReportQuery(r.within, schema);
      if (!w.ok) return { ok: false, error: `The question this one stays within: ${w.error}` };
      within = w.value;
    }
    const rate = parseRate(r.rate, splitBy, measure);
    return { ok: true, value: { query: q.value, groupBy, splitBy, measure, ...(within ? { within } : {}), ...(rate ? { rate } : {}) } };
  } catch (e) {
    if (e instanceof Invalid) return { ok: false, error: e.message };
    throw e;
  }
}
